// frontend/src/components/RegionProvinceSelector.js

import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, MapPin, Map } from 'lucide-react';

const REGIONS_PROVINCES = {
  'Abruzzo': ['L\'Aquila', 'Chieti', 'Pescara', 'Teramo'],
  'Basilicata': ['Matera', 'Potenza'],
  'Calabria': ['Catanzaro', 'Cosenza', 'Crotone', 'Reggio Calabria', 'Vibo Valentia'],
  'Campania': ['Avellino', 'Benevento', 'Caserta', 'Napoli', 'Salerno'],
  'Emilia-Romagna': ['Bologna', 'Ferrara', 'Forlì-Cesena', 'Modena', 'Parma', 'Piacenza', 'Ravenna', 'Reggio Emilia', 'Rimini'],
  'Friuli-Venezia Giulia': ['Gorizia', 'Pordenone', 'Trieste', 'Udine'],
  'Lazio': ['Frosinone', 'Latina', 'Rieti', 'Roma', 'Viterbo'],
  'Liguria': ['Genova', 'Imperia', 'La Spezia', 'Savona'],
  'Lombardia': ['Bergamo', 'Brescia', 'Como', 'Cremona', 'Lecco', 'Lodi', 'Mantova', 'Milano', 'Monza e della Brianza', 'Pavia', 'Sondrio', 'Varese'],
  'Marche': ['Ancona', 'Ascoli Piceno', 'Fermo', 'Macerata', 'Pesaro e Urbino'],
  'Molise': ['Campobasso', 'Isernia'],
  'Piemonte': ['Alessandria', 'Asti', 'Biella', 'Cuneo', 'Novara', 'Torino', 'Verbano-Cusio-Ossola', 'Vercelli'],
  'Puglia': ['Bari', 'Barletta-Andria-Trani', 'Brindisi', 'Foggia', 'Lecce', 'Taranto'],
  'Sardegna': ['Cagliari', 'Nuoro', 'Oristano', 'Sassari', 'Sud Sardegna'],
  'Sicilia': ['Agrigento', 'Caltanissetta', 'Catania', 'Enna', 'Messina', 'Palermo', 'Ragusa', 'Siracusa', 'Trapani'],
  'Toscana': ['Arezzo', 'Firenze', 'Grosseto', 'Livorno', 'Lucca', 'Massa-Carrara', 'Pisa', 'Pistoia', 'Prato', 'Siena'],
  'Trentino-Alto Adige': ['Bolzano', 'Trento'],
  'Umbria': ['Perugia', 'Terni'],
  'Valle d\'Aosta': ['Aosta'],
  'Veneto': ['Belluno', 'Padova', 'Rovigo', 'Treviso', 'Venezia', 'Verona', 'Vicenza']
};

const RegionProvinceSelector = ({ 
  selectedRegions = [], 
  selectedProvinces = [], 
  onRegionsChange, 
  onProvincesChange,
  className = ""
}) => {
  const [expandedRegions, setExpandedRegions] = useState(new Set());
  const [regionStates, setRegionStates] = useState({});

  // Calcola lo stato di ogni regione (checked, indeterminate, unchecked)
  useEffect(() => {
    const newRegionStates = {};
    
    Object.keys(REGIONS_PROVINCES).forEach(region => {
      const provinces = REGIONS_PROVINCES[region];
      const selectedProvincesInRegion = provinces.filter(province => 
        selectedProvinces.includes(province)
      );
      
      if (selectedRegions.includes(region)) {
        newRegionStates[region] = 'checked';
      } else if (selectedProvincesInRegion.length === provinces.length) {
        newRegionStates[region] = 'checked';
      } else if (selectedProvincesInRegion.length > 0) {
        newRegionStates[region] = 'indeterminate';
      } else {
        newRegionStates[region] = 'unchecked';
      }
    });
    
    setRegionStates(newRegionStates);
  }, [selectedRegions, selectedProvinces]);

  const toggleRegion = (region) => {
    const provinces = REGIONS_PROVINCES[region];
    const isCurrentlySelected = regionStates[region] === 'checked';
    
    if (isCurrentlySelected) {
      // Deseleziona regione e tutte le sue province
      const newRegions = selectedRegions.filter(r => r !== region);
      const newProvinces = selectedProvinces.filter(p => !provinces.includes(p));
      
      onRegionsChange(newRegions);
      onProvincesChange(newProvinces);
    } else {
      // Seleziona regione e tutte le sue province
      const newRegions = [...new Set([...selectedRegions, region])];
      const newProvinces = [...new Set([...selectedProvinces, ...provinces])];
      
      onRegionsChange(newRegions);
      onProvincesChange(newProvinces);
    }
  };

  const toggleProvince = (province, region) => {
    if (selectedProvinces.includes(province)) {
      // Deseleziona provincia
      const newProvinces = selectedProvinces.filter(p => p !== province);
      // Rimuovi anche la regione se era selezionata
      const newRegions = selectedRegions.filter(r => r !== region);
      
      onProvincesChange(newProvinces);
      onRegionsChange(newRegions);
    } else {
      // Seleziona provincia
      const newProvinces = [...selectedProvinces, province];
      onProvincesChange(newProvinces);
    }
  };

  const toggleExpanded = (region) => {
    const newExpanded = new Set(expandedRegions);
    if (newExpanded.has(region)) {
      newExpanded.delete(region);
    } else {
      newExpanded.add(region);
    }
    setExpandedRegions(newExpanded);
  };

  const getCheckboxStyle = (state) => {
    switch (state) {
      case 'checked':
        return 'bg-blue-600 border-blue-600 text-white';
      case 'indeterminate':
        return 'bg-blue-100 border-blue-400 text-blue-600';
      default:
        return 'bg-white border-gray-300';
    }
  };

  const handleSelectAll = () => {
    const allRegions = Object.keys(REGIONS_PROVINCES);
    const allProvinces = Array.from(
      new Set(allRegions.flatMap((r) => REGIONS_PROVINCES[r] || []))
    ).sort();

    onRegionsChange(allRegions);
    onProvincesChange(allProvinces);
  };

  const handleClearAll = () => {
    onRegionsChange([]);
    onProvincesChange([]);
  };

  const handleSelectAllProvincesForSelectedRegions = () => {
    if (!selectedRegions || selectedRegions.length === 0) return;
    const provinces = Array.from(
      new Set(selectedRegions.flatMap((r) => REGIONS_PROVINCES[r] || []))
    ).sort();
    onProvincesChange(provinces);
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="mb-4">
        <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
          <Map className="h-4 w-4 mr-2" />
          Seleziona Regioni e Province
        </h3>
        <p className="text-xs text-gray-500">
          Clicca su una regione per selezionare tutte le sue province automaticamente
        </p>
        <div className="flex flex-wrap gap-2 mt-3">
          <button
            type="button"
            onClick={handleSelectAll}
            className="px-3 py-1.5 rounded-md text-xs border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Tutte le regioni (e province)
          </button>
          <button
            type="button"
            onClick={handleClearAll}
            className="px-3 py-1.5 rounded-md text-xs border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Pulisci
          </button>
          <button
            type="button"
            onClick={handleSelectAllProvincesForSelectedRegions}
            disabled={!selectedRegions || selectedRegions.length === 0}
            className="px-3 py-1.5 rounded-md text-xs border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Tutte le province (regioni selezionate)
          </button>
        </div>
      </div>

      <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
        {Object.entries(REGIONS_PROVINCES).map(([region, provinces]) => (
          <div key={region} className="border-b border-gray-100 last:border-b-0">
            {/* Regione */}
            <div className="flex items-center p-3 bg-gray-50 hover:bg-gray-100 transition-colors">
              <button
                type="button"
                onClick={() => toggleExpanded(region)}
                className="flex items-center text-sm text-gray-600 hover:text-gray-800 mr-3"
              >
                {expandedRegions.has(region) ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>

              <label className="flex items-center flex-1 cursor-pointer">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={regionStates[region] === 'checked'}
                    ref={el => {
                      if (el) el.indeterminate = regionStates[region] === 'indeterminate';
                    }}
                    onChange={() => toggleRegion(region)}
                    className={`h-4 w-4 rounded border-2 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 ${getCheckboxStyle(regionStates[region])}`}
                  />
                  {regionStates[region] === 'indeterminate' && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-2 h-0.5 bg-blue-600 rounded"></div>
                    </div>
                  )}
                </div>
                <span className="ml-3 text-sm font-medium text-gray-900">
                  {region}
                </span>
                <span className="ml-auto text-xs text-gray-500">
                  {provinces.filter(p => selectedProvinces.includes(p)).length}/{provinces.length}
                </span>
              </label>
            </div>

            {/* Province (espanse) */}
            {expandedRegions.has(region) && (
              <div className="bg-white">
                {provinces.map(province => (
                  <label
                    key={province}
                    className="flex items-center p-2 pl-12 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedProvinces.includes(province)}
                      onChange={() => toggleProvince(province, region)}
                      className="h-3 w-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <MapPin className="h-3 w-3 ml-2 mr-2 text-gray-400" />
                    <span className="text-sm text-gray-700">{province}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Riassunto selezione */}
      {(selectedRegions.length > 0 || selectedProvinces.length > 0) && (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <h4 className="text-sm font-medium text-blue-900 mb-2">Selezione attuale:</h4>
          {selectedRegions.length > 0 && (
            <p className="text-xs text-blue-700">
              <strong>Regioni:</strong> {selectedRegions.join(', ')}
            </p>
          )}
          {selectedProvinces.length > 0 && (
            <p className="text-xs text-blue-700 mt-1">
              <strong>Province:</strong> {selectedProvinces.join(', ')}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default RegionProvinceSelector;